import {
  Component,
  OnInit,
  Input,
  ViewContainerRef,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  ValidatorFn,
} from '@angular/forms';
import { ClientDocument, IdentityDocument } from '../../../../core/types/client-document';
import { TranslateService } from '@ngx-translate/core';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { FormComponentData } from '../../../../core/types/form-component-data';
import { ClientService } from '../../../../core/services/client.service';
import { Client } from '../../../../core/types/client';
import { saveAs } from 'file-saver';
import { LoaderService } from '../../../../core/services/loader.service';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { DateFormat } from '@app/core/types/date-format';
import { NotificationService } from '../../../../core/services/notification.service';
import { EventLogService } from '@app/core/services/event-log.service';

@Component({
  selector: 'itfg-document-edit',
  templateUrl: './document-edit.component.html',
  styleUrls: ['./document-edit.component.scss'],
  standalone: false
})
export class DocumentEditComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any;
  @Input() client: Client;
  @Output() dataChanged = new EventEmitter<FormComponentData>();
  @Output() deleteClientDocument = new EventEmitter<ClientDocument>();

  documentsDataForm: UntypedFormGroup;
  listOfDocuments: any[];
  USER_DOCUMENT_TEXT: string;
  MAX_FILE_NAME_LENGTH: number;
  showLoader: boolean;
  _unsubscribe = new Subject<void>();
  documentsColumns = [];
  fromNowDateFormat = DateFormat.FROM_NOW;

  constructor(
    public formBuilder: UntypedFormBuilder,
    public translateService: TranslateService,
    public _viewContainerRef: ViewContainerRef,
    public _dialogService: DialogProviderService,
    protected clientService: ClientService,
    public loaderService: LoaderService,
    public identityReportsService: IdentityReportsService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private eventLogService: EventLogService,
  ) {
    this.USER_DOCUMENT_TEXT = 'userDocument_';
    this.MAX_FILE_NAME_LENGTH = 64;
    this.setDocumentColumns();
  }

  ngOnInit() {
    this.clientService.onDataChange
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(data => {
        this.mapInputData(this.data);
      });
  }

  setDocumentColumns() {
    this.translate
      .get(['identityDocuments.columns.createdAt', 'identityDocuments.columns.name', 'global.actions'])
      .subscribe(translation => {
        this.documentsColumns = [
          {
            name: 'createdAt',
            label: translation['identityDocuments.columns.createdAt'],
          },
          {
            name: 'name',
            label: translation['identityDocuments.columns.name']
          },
          {
            name: 'actions',
            label: translation['global.actions'],
          },
        ];
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      this.setInitialData();
    }
  }

  setInitialData() {
    this.documentsDataForm = this.createDocumentsDataForm();
    this.mapInputData(this.data);
    this.watchForFormChanges();
  }

  createDocumentsDataForm() {
    return this.formBuilder.group({});
  }

  addDocumentField(file: File) {
    const documentsListLastElement = this.listOfDocuments[
      this.listOfDocuments.length - 1
    ];
    const elementIndex =
      this.listOfDocuments.length < 1 ? 0 : documentsListLastElement.index + 1;

    this.listOfDocuments.push({
      name: file.name,
      type: '',
      id: null,
      index: elementIndex,
      file: file,
      isNew: true
    });

    this.addControlByIndex(
      this.documentsDataForm,
      this.USER_DOCUMENT_TEXT + elementIndex,
      file,
    );

    this.emitFormState();
  }

  addControlByIndex(
    form: UntypedFormGroup,
    controlName: string,
    value: any,
    validators: ValidatorFn[] = []
  ) {
    const valueControl: UntypedFormControl = this.formBuilder.control(
      value,
      validators
    );
    form.addControl(controlName, valueControl);
  }

  deleteDocumentRow(clientDocument: ClientDocument) {
    const index = this.listOfDocuments.findIndex(doc => doc.id === clientDocument.id);

    const selectedControl = this.documentsDataForm.controls[
      this.USER_DOCUMENT_TEXT + this.listOfDocuments[index].index
    ];

    this.translateService
      .get(
        [
          'clients.deleteDocument',
          'global.confirm',
          'global.cancel',
          'global.delete',
        ],
        {
          document: selectedControl.value ?? '',
        }
      )
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(translation => {
        const dialogConfig = {
          message: translation['clients.deleteDocument'],
          disableClose: false,
          viewContainerRef: this._viewContainerRef,
          title: translation['global.delete'],
          cancelButton: translation['global.cancel'],
          acceptButton: translation['global.confirm'],
        };

        if (selectedControl?.value && this.listOfDocuments.find(d => d.name === selectedControl.value)) {
          this._dialogService
            .openConfirm(dialogConfig)
            .afterClosed()
            .pipe(takeUntil(this._unsubscribe))
            .subscribe((accept: boolean) => {
              if (accept) {
                if (this.listOfDocuments[index].id) {
                  this.deleteClientDocument.emit(this.listOfDocuments[index]);
                }

                this.documentsDataForm.removeControl(
                  this.USER_DOCUMENT_TEXT + this.listOfDocuments[index].index
                );
                this.listOfDocuments.splice(index, 1);
                this.emitFormState();
                this.eventLogService.refreshEventLog.next();

                this.notificationService.showLocalizedSuccessMessage({
                  notificationText: "mi.idCardImageDeleteSuccessMessage"
                })
              }
            });
        } else {
          this.documentsDataForm.removeControl(
            this.USER_DOCUMENT_TEXT + this.listOfDocuments[index].index
          );
          this.listOfDocuments.splice(index, 1);
          this.emitFormState();
        }
      });
  }

  mapInputData(dataToMap) {
    if (!dataToMap || dataToMap.length === 0) {
      this.listOfDocuments = [];
      return;
    }

    this.listOfDocuments = [];

    for (let i = 0; i < dataToMap.length; i++) {
      this.listOfDocuments.push({
        name: dataToMap[i].name,
        id: dataToMap[i].id,
        createdAt: dataToMap[i].createdAt,
        index: i,
        type: dataToMap[i].hasOwnProperty('type') ? dataToMap[i].type?.name : '',
        isNew: false
      });

      if (
        this.documentsDataForm.controls.hasOwnProperty(
          this.USER_DOCUMENT_TEXT + i
        )
      ) {
        this.documentsDataForm.patchValue({
          [this.USER_DOCUMENT_TEXT + i]: dataToMap[i].name,
        });
      } else {
        this.addControlByIndex(
          this.documentsDataForm,
          this.USER_DOCUMENT_TEXT + i,
          dataToMap[i].name
        );
      }
    }
  }

  watchForFormChanges() {
    this.documentsDataForm.valueChanges
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => {
        this.emitFormState();
      });
  }

  emitFormState() {
    const hasUnsavedDocuments = this.hasUnsavedDocuments();

    this.dataChanged.emit({
      valid: this.documentsDataForm.valid,
      data: this.listOfDocuments,
      dirty: hasUnsavedDocuments,
    });
  }

  hasUnsavedDocuments(): boolean {
    return this.listOfDocuments.some(doc => doc.isNew === true);
  }

  saveAllPendingDocuments(clientId: number): Promise<ClientDocument[]> {
    const pendingDocuments = this.listOfDocuments.filter(doc => doc.isNew);

    if (pendingDocuments.length === 0) {
      return Promise.resolve([]);
    }

    const savePromises = pendingDocuments.map(doc => {
      const formData = new FormData();
      formData.append('file', doc.file);

      return firstValueFrom(
        this.clientService.addClientDocument(clientId, formData)
      );
    });

    return Promise.all(savePromises)
      .then(savedDocs => {
        savedDocs.forEach((savedDoc, i) => {
          const originalDoc = pendingDocuments[i];
          const originalIndex = this.listOfDocuments.findIndex(
            d => d.index === originalDoc.index
          );

          if (originalIndex !== -1) {
            this.listOfDocuments[originalIndex] = {
              ...savedDoc,
              index: originalDoc.index,
              isNew: false
            };
          }
        });

        this.emitFormState();
        return savedDocs;
      })
      .catch((error) => {
        console.error('Error saving documents:', error);
        throw error;
      });
  }

  downloadDocumentClicked(event: Event, document: ClientDocument | IdentityDocument) {
    event.stopPropagation();

    if (document.hasOwnProperty('type') && !!document['type']) {
      this.identityReportsService
        .renderIdentityDocumentContent(this.client.civilId, document.id)
        .pipe(takeUntil(this._unsubscribe))
        .subscribe(doc => {
          const blob = new Blob(<any>[doc]);
          saveAs(blob, document.name);
        });
    } else {
      this.clientService
        .renderClientDocument(this.client.id, document.id)
        .pipe(takeUntil(this._unsubscribe))
        .subscribe(doc => {
          const blob = new Blob(<any>[doc]);
          saveAs(blob, document.name);
        });
    }
  }

  onFileSelected(file: File) {
    this.addDocumentField(file);
  }

  inputControl(event) {
    const hasUnsaved = this.hasUnsavedDocuments();
    if (!hasUnsaved) {
      event.preventDefault();
    }
  }

  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}